import { supabase } from "@/integrations/supabase/client";
import { BusinessSubmission } from "@/types/business";
import { uploadMedia } from "./mediaService";
import { v4 as uuidv4 } from "uuid";

export const submitBusiness = async (data: BusinessSubmission) => {
  console.log("Submission Process Started", {
    businessName: data.name,
    mediaFilesCount: data.mediaFiles?.length || 0,
    approved: data.approved,
  });

  // Check authentication status for debugging
  const { data: authData, error: authError } = await supabase.auth.getSession();
  console.log("Authentication status:", {
    hasSession: !!authData.session,
    isAnonymous: true,
    authError: authError ? JSON.stringify(authError) : null,
  });

  // Validate required fields
  const requiredFields = ["name", "description", "category"];
  const missingFields = requiredFields.filter(
    (field) =>
      !data[field as keyof BusinessSubmission] ||
      (typeof data[field as keyof BusinessSubmission] === "string" &&
        (data[field as keyof BusinessSubmission] as string).trim() === "")
  );

  if (missingFields.length > 0) {
    console.error("Missing required fields", { missingFields });
    throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
  }

  try {
    // Generate a UUID for the business first
    const businessId = uuidv4();
    let mediaUrls: string[] = [];

    // Handle media upload first if we have files
    if (data.mediaFiles && data.mediaFiles.length > 0) {
      console.log(
        `Uploading ${data.mediaFiles.length} media files for business ID ${businessId}`
      );

      const { mediaUrls: uploadedUrls, uploadErrors } = await uploadMedia(
        data.mediaFiles,
        businessId
      );

      if (uploadErrors.length > 0) {
        console.warn("Some files failed to upload", { uploadErrors });
      }

      if (uploadedUrls.length > 0) {
        mediaUrls = uploadedUrls;
        console.log(`Successfully uploaded ${uploadedUrls.length} files`);
      }
    }

    // Now insert the business with the media URLs
    console.log("Inserting business with media URLs:", mediaUrls);

    const insertData = {
      id: businessId, // Use the pre-generated UUID
      name: data.name,
      description: data.description,
      address: data.address,
      city: data.city,
      county: data.county,
      category: data.category,
      tags: data.tags,
      website: data.website,
      phone: data.phone,
      email: data.email,
      facebook: data.facebook,
      instagram: data.instagram,
      tiktok: data.tiktok,
      region: data.region,
      postcode: data.postcode,
      what3words: data.what3words,
      approved: false,
      media_urls: mediaUrls,
    };

    const { data: responseData, error } = await supabase
      .from("businesses")
      .insert([insertData])
      .select();

    if (error) {
      console.error("Business Insertion Error", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        fullError: JSON.stringify(error),
      });
      throw error;
    }

    if (!responseData || responseData.length === 0) {
      console.error("No response data from insertion");
      throw new Error("Business insertion failed");
    }

    console.log("Business Inserted Successfully", {
      businessId,
      mediaCount: mediaUrls.length,
    });

    return responseData;
  } catch (error: unknown) {
    console.error("Comprehensive Submission Error", error);
    throw error;
  }
};
